import { useEffect } from 'react';
import { useCanAutoFocusInput } from '@/hooks/useCanAutoFocusInput';
import { focusFirstEmptyInputIfAppropriate } from '@/lib/inputFocus';

const RELEVANT_SELECTOR =
  'input, textarea, [role="dialog"], [role="alertdialog"], .MuiModal-root, .MuiPopover-root';

function nodeCouldContainInput(node: Node): boolean {
  if (!(node instanceof HTMLElement)) return false;
  return node.matches(RELEVANT_SELECTOR) || !!node.querySelector(RELEVANT_SELECTOR);
}

function getMutationFocusScopes(mutations: MutationRecord[]): ParentNode[] {
  const scopes: ParentNode[] = [];

  for (const mutation of mutations) {
    if (mutation.type === 'childList') {
      for (const node of Array.from(mutation.addedNodes)) {
        if (nodeCouldContainInput(node) && node instanceof HTMLElement) {
          scopes.push(node);
        }
      }
      continue;
    }

    if (mutation.type === 'attributes' && nodeCouldContainInput(mutation.target) && mutation.target instanceof HTMLElement) {
      scopes.push(mutation.target);
    }
  }

  return scopes;
}

export function DesktopInputFocusManager() {
  const canAutoFocusInput = useCanAutoFocusInput();

  useEffect(() => {
    if (!canAutoFocusInput) return undefined;

    let focusTimer: number | undefined;
    let pendingScopes: ParentNode[] = [];
    let documentWidePass = false;

    const scheduleFocus = (scopes: ParentNode[] = [], includeDocumentWide = false) => {
      if (scopes.length > 0) {
        pendingScopes = [...pendingScopes, ...scopes];
      }
      if (includeDocumentWide) documentWidePass = true;
      if (focusTimer !== undefined) window.clearTimeout(focusTimer);
      focusTimer = window.setTimeout(() => {
        const scopesToCheck = pendingScopes;
        const runDocumentWide = documentWidePass;
        pendingScopes = [];
        documentWidePass = false;

        for (const scope of [...scopesToCheck].reverse()) {
          if (
            focusFirstEmptyInputIfAppropriate(document, {
              scope,
              ignoreFocusOutsideScope: true,
            })
          ) {
            return;
          }
        }

        if (runDocumentWide) {
          focusFirstEmptyInputIfAppropriate(document);
        }
      }, 80);
    };

    scheduleFocus([], true);

    const observer = new MutationObserver((mutations) => {
      const scopes = getMutationFocusScopes(mutations);
      if (scopes.length === 0) return;
      scheduleFocus(scopes);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      // Visibility/disabled only — not class/style. MUI focus rings mutate those on every
      // Tab and would re-enter this path as a focus loop.
      attributeFilter: ['aria-hidden', 'disabled', 'hidden', 'readonly'],
    });

    return () => {
      if (focusTimer !== undefined) window.clearTimeout(focusTimer);
      observer.disconnect();
    };
  }, [canAutoFocusInput]);

  return null;
}
